import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiStar, FiCheckCircle } from 'react-icons/fi';
import { api } from '../../utils/api';
import { triggerRefresh, REFRESH_EVENTS } from '../../utils/refreshEvents';
import './AdminPricing.css';

const AdminPricing = () => {
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'assistance',
    type: 'carte',
    price: 0,
    unit: '€',
    period: '',
    description: '',
    features: [],
    popular: false,
    active: true,
    order: 0
  });
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const res = await api.getPricing();
      setPricing(res.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPricing) {
        await api.updatePricing(editingPricing._id, formData);
      } else {
        await api.createPricing(formData);
      }
      fetchPricing();
      resetForm();
      // Déclencher le rafraîchissement sur les pages publiques
      triggerRefresh(REFRESH_EVENTS.PRICING);
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (item) => {
    setEditingPricing(item);
    setFormData({
      name: item.name,
      category: item.category,
      type: item.type,
      price: item.price,
      unit: item.unit || '€',
      period: item.period || '',
      description: item.description || '',
      features: item.features || [],
      popular: item.popular || false,
      active: item.active,
      order: item.order || 0
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce tarif ?')) {
      try {
        await api.deletePricing(id);
        fetchPricing();
        // Déclencher le rafraîchissement sur les pages publiques
        triggerRefresh(REFRESH_EVENTS.PRICING);
      } catch (error) {
        alert('Erreur lors de la suppression');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'assistance',
      type: 'carte',
      price: 0,
      unit: '€',
      period: '',
      description: '',
      features: [],
      popular: false,
      active: true,
      order: 0
    });
    setEditingPricing(null);
    setShowForm(false);
    setNewFeature('');
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <div className="loading-admin">
        <div className="loading-spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="admin-pricing">
      <motion.div
        className="admin-header-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2>Gestion des Tarifs</h2>
        <motion.button
          onClick={() => setShowForm(!showForm)}
          className="btn btn-primary"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {showForm ? (
            <>
              <FiX />
              <span>Annuler</span>
            </>
          ) : (
            <>
              <FiPlus />
              <span>Ajouter un tarif</span>
            </>
          )}
        </motion.button>
      </motion.div>

      <AnimatePresence>
        {showForm && (
          <motion.form
            onSubmit={handleSubmit}
            className="pricing-form"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="form-header">
              <h3>{editingPricing ? 'Modifier le tarif' : 'Nouveau tarif'}</h3>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Nom *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Ex: Essentiel"
                />
              </div>
              <div className="form-group">
                <label>Catégorie *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  <option value="assistance">Assistance & Communication</option>
                  <option value="specifique">Services Spécifiques</option>
                  <option value="coaching">Coaching & Stratégie</option>
                </select>
              </div>
              <div className="form-group">
                <label>Type *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                >
                  <option value="carte">À la carte</option>
                  <option value="forfait">Forfait</option>
                  <option value="service">Service</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Prix *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                  required
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Unité</label>
                <input
                  type="text"
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  placeholder="€"
                />
              </div>
              <div className="form-group">
                <label>Période</label>
                <input
                  type="text"
                  value={formData.period}
                  onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                  placeholder="/h, /mois, etc."
                />
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="2"
                placeholder="Description du tarif..."
              />
            </div>

            {formData.type === 'forfait' && (
              <div className="form-group">
                <label>Caractéristiques</label>
                <div className="features-input">
                  <input
                    type="text"
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                    placeholder="Ajouter une caractéristique"
                  />
                  <motion.button
                    type="button"
                    onClick={addFeature}
                    className="btn btn-secondary"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FiPlus />
                  </motion.button>
                </div>
                {formData.features.length > 0 && (
                  <ul className="features-list">
                    {formData.features.map((feature, index) => (
                      <motion.li
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                      >
                        <FiCheckCircle className="feature-check-icon" />
                        <span>{feature}</span>
                        <button
                          type="button"
                          onClick={() => removeFeature(index)}
                          className="remove-feature-btn"
                        >
                          <FiX />
                        </button>
                      </motion.li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Ordre d'affichage</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  min="0"
                />
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.popular}
                    onChange={(e) => setFormData({ ...formData, popular: e.target.checked })}
                  />
                  <span>Mettre en avant (Populaire)</span>
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  />
                  <span>Tarif actif</span>
                </label>
              </div>
            </div>

            <div className="form-actions">
              <motion.button
                type="submit"
                className="btn btn-primary"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiSave />
                <span>{editingPricing ? 'Modifier' : 'Créer'}</span>
              </motion.button>
              {editingPricing && (
                <motion.button
                  type="button"
                  onClick={resetForm}
                  className="btn btn-secondary"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <FiX />
                  <span>Annuler</span>
                </motion.button>
              )}
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      <motion.div
        className="pricing-list"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        {pricing.map((item, index) => (
          <motion.div
            key={item._id}
            className="pricing-item"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.05 }}
            whileHover={{ y: -4, boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)" }}
          >
            <div className="pricing-info">
              <div className="pricing-header">
                <h3>{item.name}</h3>
                {item.popular && (
                  <span className="popular-badge">
                    <FiStar className="star-icon-small" />
                    Populaire
                  </span>
                )}
              </div>
              <p className="pricing-category">{item.category} - {item.type}</p>
              <div className="pricing-price-display">
                <span className="price">{item.price}</span>
                <span className="period">{item.unit}{item.period}</span>
              </div>
              {item.description && <p>{item.description}</p>}
              {item.features && item.features.length > 0 && (
                <ul>
                  {item.features.map((feature, i) => (
                    <li key={i}>
                      <FiCheckCircle className="feature-bullet" />
                      {feature}
                    </li>
                  ))}
                </ul>
              )}
              <span className={`status ${item.active ? 'active' : 'inactive'}`}>
                {item.active ? 'Actif' : 'Inactif'}
              </span>
            </div>
            <div className="pricing-actions">
              <motion.button
                onClick={() => handleEdit(item)}
                className="btn btn-secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FiEdit2 />
                <span>Modifier</span>
              </motion.button>
              <motion.button
                onClick={() => handleDelete(item._id)}
                className="btn btn-danger"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FiTrash2 />
                <span>Supprimer</span>
              </motion.button>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

export default AdminPricing;
